import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {cloneDeep} from 'lodash';
import {ServiceAction, ServiceState} from '../../models/service';
import {StorageService} from '../../services/storage/storage.service';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {Logger} from '../../class/helper/logger.class';
import {Loader} from '../../models/loader/loader.redux';
import {IRecordEmit} from '../../interfaces/record';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {ActivatedRoute, Params} from '@angular/router';
import {IUser} from 'src/app/models/auth/user.interface';
import {IOtherHabits, OtherHabitsState, RecordOtherHabitsAction} from '../../models/record-other-habits';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Store} from '@ngxs/store';
import {AuthState} from '../../models/auth';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {AppTourService} from '../../services/app-tour/app-tour.service';
import {SkeletonService} from '../../services/skeleton/skeleton.service';
import {SkeletonEnum} from '../../enums/skeleton';

@Component({
  selector: 'app-record-history-habits',
  templateUrl: './record-history-habits.component.html',
  styleUrls: ['./record-history-habits.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryHabitsComponent implements OnInit, OnDestroy {
  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(OtherHabitsState.getWasDeployed);
  frequencies: any[] = [];

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;
  limit = 100;
  userLogged: IUser;
  @Input() otherHabits: IOtherHabits;
  @Output() otherHabitsChange = new EventEmitter<IRecordEmit>();
  otherHabitsRedux: IOtherHabits;

  form: UntypedFormGroup;
  disableForm: boolean;

  wasDeployedSubscription: Subscription;

  loadInfo: boolean;
  skeletonTemplate: any[] = [];

  options: {label: string, value: number}[] = [];
  constructor(
    private appTourService: AppTourService,
    public utilitiesService: UtilitiesService,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private messageAlert: MessageAlertService,
    private storageService: StorageService,
    private skeletonService: SkeletonService,
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.otherHabits) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.frequencies = [
      {
        label: 'No lo acostumbro',
        value: 0
      },
      {
        label: '1 a 2 días a la semana',
        value: 1
      },
      {
        label: '3 a 4 días a la semana',
        value: 3
      },
      {
        label: '5 a 7 días a la semana',
        value: 5
      }
    ];
    this.options = [
      {
        label: 'Nunca',
        value: 0
      },
      {
        label: 'Casi nunca',
        value: 1
      },
      {
        label: 'De vez en cuando',
        value: 2
      },
      {
        label: 'A menudo',
        value: 3
      },
      {
        label: 'Muy a menudo',
        value: 4
      }
    ];
    this.initValues();
  }

  initValues(): void {
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
  }

  /**
   * Lanza las peticiones iniciales necesarias al servidor.
   */
  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando hábitos del paciente ...')));
      this.getParams();
      this.userLogged = this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user;
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordOtherHabitsAction.GetActive(this.serviceId, this.patientId)));
      this.otherHabitsRedux = cloneDeep(await firstValueFrom(this.store.selectOnce(OtherHabitsState.getOtherHabits)));
      this.otherHabits = cloneDeep(this.otherHabitsRedux);
      this.otherHabitsChange.emit({ record: this.otherHabits, updated: false });
      this.setFormProperties(this.otherHabitsRedux);
      setTimeout(() => {
        this.loadInfo = true;
      });
    } catch (e) {
      Logger.log('Ocurrió un error here', e);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(otherHabits: IOtherHabits): void {
    this.disableForm = !this.userLogged?.experts;
    this.form = new UntypedFormGroup({
      drinkAlcohol: new UntypedFormControl({ value: otherHabits ? otherHabits.drinkAlcohol || false : false, disabled: this.disableForm }),
      drinkAlcoholFrequency: new UntypedFormControl({ value: otherHabits ? otherHabits.drinkAlcoholFrequency || 0 : 0, disabled: this.disableForm }),
      cupAlcoholPerDay: new UntypedFormControl({ value: otherHabits ? otherHabits.cupAlcoholPerDay || 0 : 0, disabled: this.disableForm }),
      drinkCoffee: new UntypedFormControl({ value: otherHabits ? otherHabits.drinkCoffee || false : false, disabled: this.disableForm }),
      drinkCoffeeFrequency: new UntypedFormControl({ value: otherHabits ? otherHabits.drinkCoffeeFrequency || 0 : 0, disabled: this.disableForm }),
      cupCoffeePerDay: new UntypedFormControl({ value: otherHabits ? otherHabits.cupCoffeePerDay || 0 : 0, disabled: this.disableForm }),
      smoke: new UntypedFormControl({ value: otherHabits ? otherHabits.smoke || false : false, disabled: this.disableForm }),
      smokeFrequency: new UntypedFormControl({ value: otherHabits ? otherHabits.smokeFrequency || 0 : 0, disabled: this.disableForm }),
      cigarettesPerDay: new UntypedFormControl({ value: otherHabits ? otherHabits.cigarettesPerDay || 0 : 0, disabled: this.disableForm }),
      howOftenHaveFeltUnableToControlImportantThings: new UntypedFormControl({
        value: otherHabits.howOftenHaveFeltUnableToControlImportantThings,
        disabled: this.disableForm
      }),
      howOftenHaveBeenConfidentAboutAbilityToHandlePersonalProblems: new UntypedFormControl({
        value: otherHabits.howOftenHaveBeenConfidentAboutAbilityToHandlePersonalProblems,
        disabled: this.disableForm
      }),
      howOftenHaveFeltThatThingsAreGoingWell: new UntypedFormControl({
        value: otherHabits.howOftenHaveFeltThatThingsAreGoingWell,
        disabled: this.disableForm
      }),
      howOftenHaveFeltThatDifficultiesArePilingUpSoMuchThatCanOvercomeThem: new UntypedFormControl({
        value: otherHabits.howOftenHaveFeltThatDifficultiesArePilingUpSoMuchThatCanOvercomeThem,
        disabled: this.disableForm
      }),
    });

    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  emitChangeUpdate(): void {
    this.otherHabits.updatedAt = new Date().toISOString();
    this.otherHabitsChange.emit({ record: this.otherHabits, updated: true });
  }

  async onSave(): Promise<void> {
    // Tour mode
    if (this.appTourService.tourMode) {
      return;
    }

    // Normal flow
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando hábitos ...'))));
      if (!this.otherHabitsRedux._id) {
        await firstValueFrom((this.store.dispatch(new RecordOtherHabitsAction.Post(this.serviceId, this.patientId, this.form.getRawValue()))));
        const otherHabitsCreated: IOtherHabits = await firstValueFrom((this.store.selectOnce(OtherHabitsState.getOtherHabits)));
        this.otherHabitsRedux._id = otherHabitsCreated?._id || null;
      } else {
        await firstValueFrom(this.store.dispatch(new RecordOtherHabitsAction.PatchActive(
          this.serviceId, this.patientId, this.otherHabitsRedux._id, this.form.getRawValue())
        ));
      }
      this.messageAlert.createMessage('success', 'Hábitos guardados exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar los hábitos. Intente más tarde', 5000);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  async ngOnDestroy(): Promise<void> {
    await firstValueFrom((this.store.dispatch(new RecordOtherHabitsAction.ResetState())));
    this.wasDeployedSubscription.unsubscribe();
  }
}
